import React, { useState, useEffect, useRef, useCallback } from "react";
import { updateInMatchchatGroupName, fetchGroupMessagesContent, getCurrentUserIdFromToken, sendAppointmentInMatchChat, fetchGroupAppointment } from "../../Api";
import { wsIp } from "@/lib/axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { FaUpload } from "react-icons/fa"; // upload icon

function GroupMessageModal({ chatName, chatId, onClose, onGroupNameUpdated }) {
    const [chatHistory, setChatHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1); // Track current page
    const [totalPages, setTotalPages] = useState(1); // Track total pages
    const [hasMore, setHasMore] = useState(true); // If more pages exist
    const [message, setMessage] = useState("");
    const [showCalendarDock, setShowCalendarDock] = useState(false);
    const [scheduleName, setScheduleName] = useState("");
    const [appointmentDate, setAppointmentDate] = useState("");
    const [appointmentTime, setAppointmentTime] = useState("");
    const [isVisible, setIsVisible] = useState(false);

    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null); // For scroll events
    const wsRef = useRef(null);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [userMap, setUserMap] = useState({});
    // refs to coordinate scroll behavior
    const isLoadingOlderRef = useRef(false); // true while we are prepending older messages and restoring scroll
    const lastChatHistoryLengthRef = useRef(0);
    const initialLoadDoneRef = useRef(false); // mark when first page load completed

    const [editingChatName, setEditingChatName] = useState(false);
    const [chatNameInput, setChatNameInput] = useState(chatName);
    const typingTimeoutRef = useRef(null);

    const handleChatNameChange = async (newName) => {
        try {
            await updateInMatchchatGroupName({ name: newName }, chatId);
            if (onGroupNameUpdated) onGroupNameUpdated(); // notify parent to refetch
        } catch (err) {
            console.error("Failed to update chat name", err);
        }
    };

    const [appointment, setAppointment] = useState(null);
    useEffect(() => {
        if (!chatId) return;

        const loadAppointment = async () => {
            try {
                const data = await fetchGroupAppointment(chatId);
                if (Array.isArray(data) && data.length > 0) {
                    const app = data[0];
                    setAppointment(app);

                    // Pre-fill form fields with appointment values
                    setScheduleName(app.schedule_name || "");
                    setAppointmentDate(app.date || "");
                    setAppointmentTime(app.hour || "");
                } else {
                    setAppointment(null);
                    setScheduleName("");
                    setAppointmentDate("");
                    setAppointmentTime("");
                }
            } catch (err) {
                console.error("Failed to fetch appointment", err);
                setAppointment(null);
            }
        };

        loadAppointment();
    }, [chatId]);
    useEffect(() => {
        if (!chatId) return;

        const loadAppointment = async () => {
            try {
                const data = await fetchGroupAppointment(chatId);
                if (Array.isArray(data) && data.length > 0) {
                    setAppointment(data[0]); // first appointment (or you can handle multiple)
                } else {
                    setAppointment(null);
                }
            } catch (err) {
                console.error("Failed to fetch appointment", err);
                setAppointment(null);
            }
        };

        loadAppointment();
    }, [chatId]);

const getPHNow = () => {
  const now = new Date();

  const date = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Manila",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now).replace(/\//g, "-");

  const time = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Manila",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(now);

  return { date, time };
};

const { date: todayDate, time: currentTime } = getPHNow();

   const handleSendAppointment = async () => {
    if (!scheduleName || !appointmentDate || !appointmentTime) {
        alert("Please fill out all appointment fields.");
        return;
    }

    const [y, m, d] = appointmentDate.split("-").map(Number);
    const [hh, mm] = appointmentTime.split(":").map(Number);

    const selected = new Date(y, m - 1, d, hh, mm);
    const now = new Date();

    if (isNaN(selected.getTime())) {
        alert("Invalid date or time.");
        return;
    }

    if (selected.getTime() <= now.getTime()) {
        alert("Date and time must be in the future.");
        return;
    }

    try {
        const payload = {
            schedule_name: scheduleName,
            date: appointmentDate,
            hour: appointmentTime,
            target_id: chatId,
            type: "match",
        };

        const newAppointment = await sendAppointmentInMatchChat(payload);
        setAppointment(newAppointment);
        setShowCalendarDock(false);
    } catch (err) {
        alert(err?.message || "Failed to send appointment");
    }
};


    // Fade in on mount
    useEffect(() => {
        setIsVisible(true);
    }, []);

    const handleClose = () => {
        setIsVisible(false);
        onClose(); // call onClose immediately without any delay
    };

    useEffect(() => {
        async function fetchCurrentUser() {
            const id = await getCurrentUserIdFromToken();
            setCurrentUserId(id);
        }
        fetchCurrentUser();
    }, []);

    // Fetch messages with pagination
    const fetchMessages = useCallback(async (pageToLoad = 1) => {
        if (!chatId) return;
        setLoading(true);
        setError(null);
        try {
            const data = await fetchGroupMessagesContent(chatId, pageToLoad);
            // Format messages
            const formattedMessages = data.messages.map((msg, index) => ({
                id: msg.id ?? index,
                text: msg.content,
                sentAt: msg.sent_at ? new Date(msg.sent_at).getTime() : Date.now(),
                fullName: msg.full_name || "Unknown",
                senderId: msg.sender_id,
            }));

            // Apply fetched messages immediately for smooth display
            if (pageToLoad === 1) {
                setChatHistory(formattedMessages.reverse()); // Newest at bottom
                // mark first load finished (prevents mis-detect when user scrolls before effect ran)
                initialLoadDoneRef.current = true;
            } else {
                setChatHistory((prev) => [...formattedMessages.reverse(), ...prev]);
            }
            setPage(data.page);
            setTotalPages(data.total_pages);
            setHasMore(data.page < data.total_pages);
        } catch {
            setError("Failed to load chat.");
        } finally {
            setLoading(false);
        }
    }, [chatId]);

    // Initial load
    useEffect(() => {
        setChatHistory([]);
        setPage(1);
        setHasMore(true);
        fetchMessages(1);
    }, [chatId, fetchMessages]);

    // Infinite scroll: load more when scrolled to top, and preserve scroll position
    useEffect(() => {
        const container = messagesContainerRef.current;
        if (!container) return;

        let prevScrollHeight = 0;

        const handleScroll = async () => {
            if (container.scrollTop === 0 && hasMore && !loading) {
                // mark that we're loading older messages so auto-scroll won't run
                isLoadingOlderRef.current = true;
                // Save current scroll height before loading more
                prevScrollHeight = container.scrollHeight;
                const prevScrollTop = container.scrollTop;

                await fetchMessages(page + 1);

                // After messages are prepended, restore scroll position
                // wait for layout to settle (double RAF) then restore the relative scroll offset
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        if (container && prevScrollHeight) {
                            const newScrollHeight = container.scrollHeight;
                            container.scrollTop = newScrollHeight - prevScrollHeight + prevScrollTop;
                        }
                        // give a tiny buffer before re-enabling auto-scroll
                        setTimeout(() => {
                            isLoadingOlderRef.current = false;
                        }, 40);
                    });
                });
            }
        };

        container.addEventListener("scroll", handleScroll);
        return () => container.removeEventListener("scroll", handleScroll);
    }, [fetchMessages, hasMore, loading, page]);

    useEffect(() => {
        if (!chatId || chatHistory.length === 0) return;

        const map = {};
        chatHistory.forEach((msg) => {
            if (msg.senderId && msg.fullName) {
                map[msg.senderId] = msg.fullName;
            }
        });
        setUserMap(map);

        if (wsRef.current) {
            wsRef.current.close();
        }

        const token = localStorage.getItem("authToken");
        const ws = new WebSocket(`${wsIp}/ws/match/in_match_chat/${chatId}?token=${token}`);
        wsRef.current = ws;

        ws.onmessage = (event) => {
            try {
                const msg = JSON.parse(event.data);
                if (msg.error) {
                    setError(msg.error);
                    return;
                }

                const senderId = msg.sender_id;
                const fullName = msg.sender_full_name || map[senderId] || "Unknown";

                const incomingMessage = {
                    id: msg.id ?? Date.now(),
                    text: msg.content,
                    sentAt: msg.sent_at ? new Date(msg.sent_at).getTime() : Date.now(),
                    fullName,
                    senderId,
                };

                setChatHistory((prev) => [...prev, incomingMessage]);

                if (msg.sender_full_name && senderId) {
                    setUserMap((prev) => ({ ...prev, [senderId]: msg.sender_full_name }));
                }
            } catch (e) {
                console.error("Failed to parse WS message", e);
            }
        };

        return () => {
            ws.close();
        };
    }, [chatId, chatHistory]);


    useEffect(() => {
        const container = messagesContainerRef.current;

        if (isLoadingOlderRef.current) {
            lastChatHistoryLengthRef.current = chatHistory.length;
            return;
        }

        if (!container) {
            if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
            lastChatHistoryLengthRef.current = chatHistory.length;
            return;
        }

        const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
        const NEAR_BOTTOM_THRESHOLD = 150; // px

        const prevLen = lastChatHistoryLengthRef.current || 0;
        const isInitialLoad = initialLoadDoneRef.current && prevLen === 0 && chatHistory.length > 0;

        if (isInitialLoad || distanceFromBottom < NEAR_BOTTOM_THRESHOLD) {
            if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }

        lastChatHistoryLengthRef.current = chatHistory.length;
    }, [chatHistory]);

    const handleSend = () => {
        if (!message.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
        wsRef.current.send(JSON.stringify({ content: message.trim() }));
        setMessage("");
    };

    const formatTime = (time) =>
        new Date(time).toLocaleString([], {
            year: "numeric",
            month: "numeric",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
        });

    return (
        <div
            onClick={handleClose}
            style={{
                position: "fixed",
                inset: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 1050,
         //       backgroundColor: "transparent", // modal overlay background removed
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    backgroundColor: "#a3a0a0", // modal background
                    borderRadius: 10,
                  //  boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
                    width: 500,
                    height: 600,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {/* Header */}
                <div
                    style={{
                        padding: 12,
                     //   borderBottom: "1px solid #eee",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                       // backgroundColor: "#d8211bff", // header background updated
                        borderTopLeftRadius: 10,
                        borderTopRightRadius: 10,
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {editingChatName ? (
                            <input
                                type="text"
                                value={chatNameInput}
                                onChange={(e) => {
                                    setChatNameInput(e.target.value);

                                    // Clear previous timeout if any
                                    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

                                    // Set new timeout to fire after user stops typing for 1.5s
                                    typingTimeoutRef.current = setTimeout(async () => {
                                        try {
                                            await handleChatNameChange(chatNameInput); // use current state
                                        } catch (err) {
                                            console.error("Failed to update chat name", err);
                                        }
                                    }, 1500);
                                }}
                                onBlur={() => {
                                    // Optional: send last update on blur
                                    if (typingTimeoutRef.current) {
                                        clearTimeout(typingTimeoutRef.current);
                                        updateInMatchchatGroupName({ name: chatNameInput }, chatId).catch(console.error);
                                    }
                                    setEditingChatName(false);
                                }}
                                autoFocus
                                style={{
                                    fontWeight: "bold",
                                    fontSize: 16,
                                    padding: 4,
                                    borderRadius: 4,
                                  //  border: "1px solid #ccc",
                                }}
                            />
                        ) : (
                            <strong
                                onClick={() => {
                                    setEditingChatName(true);
                                    toast.info("It may take a while to update the group name. For full reflection, please reload the browser.");
                                }}
                                style={{ cursor: "pointer" }}
                            >
                                {chatNameInput} {/* display current updated name */}
                            </strong>
                        )}
                    </div>
                    <button
                        onClick={handleClose}
                        style={{
                            background: "transparent",
                            border: "none",
                            fontSize: 18,
                            cursor: "pointer",
                        }}
                        aria-label="Close chat"
                    >
                        ❌
                    </button>
                </div>

                {/* Messages */}
                <div
                    ref={messagesContainerRef}
                    style={{
                        flex: 1,
                        padding: 10,
                        overflowY: "auto",
                      //  backgroundColor: "#ff9b54", // messages background
                        display: "flex",
                        flexDirection: "column",
                        gap: 12,
                        position: "relative",
                    }}
                >
                    {error && <p style={{ color: "red" }}>{error}</p>}

                    {chatHistory.map((msg) => {
                        const isCurrentUser = msg.senderId === currentUserId;
                        return (
                            <div
                                key={msg.id}
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: isCurrentUser ? "flex-end" : "flex-start",
                                    marginBottom: 8,
                                }}
                            >
                                <div
                                    style={{
                                        fontWeight: 600,
                                        fontSize: 13,
                                        marginBottom: 4,
                                      color: "#333",
                                        textAlign: isCurrentUser ? "right" : "left",
                                        maxWidth: "80%",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {msg.fullName}
                                </div>
                                <div
                                    style={{
                                        backgroundColor: isCurrentUser ? "#1976d2" : "#fff", // blue for current user, white for others
                                        color: isCurrentUser ? "#fff" : "#000", // white font for current user, black for others
                                        padding: "10px 14px",
                                        borderRadius: 16,
                                        maxWidth: "80%",
                                        wordBreak: "break-word",
                                        fontSize: 14,
                                        lineHeight: 1.4,
                                      //  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                       // border: isCurrentUser ? "none" : "1px solid #ddd", // subtle border for others
                                    }}
                                >
                                    {msg.text}
                                </div>
                                <div
                                    style={{
                                        fontSize: 12,
                                        marginTop: 4,
                                      //  color: "#0e0e0eff", // more visible gray
                                        fontStyle: "normal", // not italic
                                        textAlign: isCurrentUser ? "right" : "left",
                                        maxWidth: "80%",
                                        fontWeight: 500,
                                    }}
                                >
                                    {formatTime(msg.sentAt)}
                                </div>
                            </div>
                        );
                    })}
                    <div ref={messagesEndRef} />
                </div>

                {/* Footer with calendar and input */}
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                       // borderTop: "1px solid #ddd",
                        padding: 8,
                        gap: 8,
                      //  backgroundColor: "#ff9b54", // footer background
                    }}
                >
                    {showCalendarDock && (
                        <div
                            style={{
                             //   backgroundColor: "#fff", // calendar dock background
                                padding: "8px",
                                borderRadius: "6px",
                                display: "flex",
                                gap: "8px",
                                alignItems: "center",
                            }}
                        >
                            <div style={{ display: "flex", flexDirection: "column", flex: 1, gap: "0" }}>
                                <div style={{ display: "flex", gap: "0" }}>
                                    <div style={{ display: "flex", alignItems: "center", flex: 1, gap: "0" }}>
                                        <label style={{ fontSize: "12px", marginRight: "2px", color: "#000" }}>Date</label>
                                        <input
                                            type="date"
                                                                                          min={getPHNow().date}

                                            value={appointmentDate}
                                            onChange={(e) => setAppointmentDate(e.target.value)}
                                            style={{
                                                flex: 1,
                                                padding: "4px",
                                                borderRadius: "4px",
                                              //  border: "1px solid #ccc",
                                                fontSize: "12px",
                                                cursor: "pointer",
                                              //  color: "#000", // black font
                                            }}
                                            onClick={(e) => e.target.showPicker?.()}
                                        />
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", flex: 1, gap: "0", marginLeft: "4px" }}>
                                        <label style={{ fontSize: "12px", marginRight: "2px", color: "#000" }}>Hour</label>
                                        <input
                                            type="time"
                                            value={appointmentTime}
                                            onChange={(e) => setAppointmentTime(e.target.value)}
                                            style={{
                                                flex: 1,
                                                padding: "4px",
                                                borderRadius: "4px",
                                            //    border: "1px solid #ccc",
                                                fontSize: "12px",
                                                cursor: "pointer",
                                             //   color: "#000", // black font
                                            }}
                                            onClick={(e) => e.target.showPicker?.()}
                                            
                                        />
                                    </div>
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: "0", marginTop: "0" }}>
                                    <label style={{ fontSize: "12px", marginRight: "2px", color: "#000" }}>Schedule Name</label>
                                    <input
                                        type="text"
                                        placeholder="Title"
                                        value={scheduleName}
                                        onChange={(e) => setScheduleName(e.target.value)}
                                        style={{
                                            flex: 1,
                                            padding: "4px",
                                            borderRadius: "4px",
                                           // border: "1px solid #ccc",
                                            fontSize: "12px",
                                      //      color: "#000", // black font
                                        }}
                                    />
                                </div>
                            </div>
                            <button
                                onClick={handleSendAppointment}
                                style={{
                                    border: "none",
                                    borderRadius: "6px",
                                    padding: "10px 14px",
                                    fontSize: "30px",
                                    cursor: "pointer",
                                   // color: "#050505ff",
                                    alignSelf: "center",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                <FaUpload />
                            </button>
                        </div>
                    )}

                    {/* Inline row: calendar toggle + message input + send */}
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        {/* Calendar toggle */}
                        <button
                            onClick={() => setShowCalendarDock((prev) => !prev)}
                            style={{
                              //  backgroundColor: "#fff", // appointment toggle background
                                border: "none",
                                borderRadius: "50%",
                                width: "36px",
                                height: "36px",
                                fontSize: "18px",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#1976d2", // icon color blue
                            }}
                        >
                            📅
                        </button>

                        {/* Message input */}
                        <input
                            type="text"
                            placeholder="Type your message..."
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSend()}
                            style={{
                                flex: 1,
                                padding: 10,
                                borderRadius: 20,
                                border: "1px solid #ccc",
                                fontSize: "14px",
                            }}
                        />

                        {/* Send button */}
                        <button
                            onClick={handleSend}
                            style={{
                                padding: "0 16px",
                                borderRadius: 20,
                                backgroundColor: "#1976d2", // send button blue
                                color: "#fff", // white font
                                border: "none",
                                cursor: "pointer",
                                fontWeight: "bold",
                                fontSize: "14px",
                            }}
                        >
                            Send
                        </button>
                    </div>
                </div>

            </div>
        </div>
    );
}

export default GroupMessageModal;
